import { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Typography, Paper, Box, InputAdornment, TextField, Stack } from '@mui/material';
import {
    setCurrentPage,
    setItems,
    setPaginationMeta,
    setItemsPerPage,
    setIsLoading,
    setAllQuestionRegistriesIds,
    setUnLockedAddToRegistryQuestionCount
} from 'store/ResponseTemplate/actions';
import { SET_LOADING } from 'store/actions';
import ResponseTemplateSidebar from './Sidebar';
import ResponseTemplateContent from './Content';
import ResponseTemplateModal from './Modal';
// import MonthlyPrompt from './Content/MonthlyPrompt';
import { Clear, Search } from '@mui/icons-material';
import { debounce } from 'utils/globalFunctions';
import { setSearchText } from 'store/ResponseTemplate/actions';
import { GET_ALL_QUESTION_REGISTRIES, GET_ALL_QUESTION_REGISTRIES_IDS, GET_TOTAL_QUESTION_LOCKED_QUESTION_REGISTRIES } from 'schemas/query';
import { useLazyQuery } from '@apollo/client';
import { checkIsFreemiumPlan, checkIsPremiumPlan } from 'helper/checkPlan.helper';
import FreemiumPlanWrapper from 'Components/FreemiumPlanWrapper';
import QuestionRegistryFremiumListing from 'Components/QuestionRegistryFremiumListing';
import deleteQuestionIcon from 'assets/images/dashboard/img-deleted.svg';
import draftedQuestionIcon from 'assets/images/dashboard/img-drafts.svg';
import starredQuestion from 'assets/images/dashboard/img-starred.svg';
import { setLockedAddToRegistryQuestionCount } from 'store/ResponseTemplate/actions';
import QuestionCardInformation from './QuestionCardInformation';
import { removeDuplicatesAndModifiedFromLatestArray } from 'helper/array.helper';

function ResponseTemplate({ isNotExtension = true }) {
    const { id } = useParams();
    const dispatch = useDispatch();
    const location = useLocation();
    const { itemsPerPage, currentPage, templateType, selectedItems, searchText, reload, items } = useSelector((params) => params.response);
    const [searchTextInput, setSearchTextInput] = useState(searchText || '');
    const { companyData, authorizedEmails } = useSelector((params) => params.customization);
    const authentication = useSelector((state) => state.authentication);
    const { hasAdministrativeControl, canManageQuestion } = authentication.permissions;

    /**Get All Question Registries Id Regardless of page */
    const [getQuestionIds] = useLazyQuery(GET_ALL_QUESTION_REGISTRIES_IDS, {
        variables: {
            getQuestionRegistryIdsInput: {
                companyId: companyData?._id,
                type: templateType
            }
        },
        onCompleted: (data) => {
            const { questionRegistryIdsData } = data.getQuestionRegistryIds;
            dispatch({ type: setAllQuestionRegistriesIds, allQuestionRegistries: questionRegistryIdsData });
        }
    });

    const [getTotalLockedQuestionRegistriesCount] = useLazyQuery(GET_TOTAL_QUESTION_LOCKED_QUESTION_REGISTRIES, {
        onCompleted: (data) => {
            const { count } = data.getLockedQuestionRegistriesCount;
            dispatch({
                type: setUnLockedAddToRegistryQuestionCount,
                unLockedQuestionRegistryAddCount: count
            });
        }
    });

    const filterQuestionRegistryByTemplateType = (newItems) => {
        let filteredItems = [...newItems];

        if (templateType === 'starred') {
            filteredItems = filteredItems.filter((item) => item.starred);
        } else if (templateType === 'deleted') {
            filteredItems = filteredItems.filter((item) => item.deleted);
        } else if (templateType === 'drafted') {
            filteredItems = filteredItems.filter((item) => item.drafted);
        } else {
            filteredItems = filteredItems.filter((item) => !item.deleted);
        }

        return filteredItems;
    };

    /**Get Unlocked Question Registries*/
    // const [getTotalUnlockedQuestionRegistriesCount] = useLazyQuery(GET_TOTAL_UNLOCKED_QUESTIONS_REGISTRIES_COUNT, {
    //     onCompleted: (data) => {
    //         if (data) {
    //             const { count } = data.getUnlockedQuestionRegistriesCount;
    //             dispatch({
    //                 type: setLockedAddToRegistryQuestionCount,
    //                 lockedQuestionRegistryAddCount: count || 0
    //             });
    //         }
    //     }
    // });

    /**Get All Questions Registries for listing in page */
    const [getQuestion] = useLazyQuery(GET_ALL_QUESTION_REGISTRIES, {
        variables: {
            getQuestionRegistriesInput: {
                companyId: companyData._id,
                limit: itemsPerPage,
                page: currentPage,
                type: !isNotExtension ? 'all' : templateType,
                searchText,
                isFromExtension: !isNotExtension,
                authorizedEmails: authorizedEmails,
                userId: id
            }
        },
        onCompleted: (data) => {
            let { getQuestionRegistries } = data;

            let newItems = [];
            if (currentPage === 1) {
                newItems = [...getQuestionRegistries.items];
            } else {
                newItems = removeDuplicatesAndModifiedFromLatestArray([...items, ...getQuestionRegistries.items]);
            }

            if (isNotExtension) {
                newItems = filterQuestionRegistryByTemplateType(newItems);
            }
            dispatch({ type: setItems, items: newItems });
            dispatch({ type: setCurrentPage, currentPage: getQuestionRegistries.meta.currentPage });
            dispatch({ type: setItemsPerPage, itemsPerPage: getQuestionRegistries.meta.itemsPerPage });
            dispatch({ type: setPaginationMeta, paginationMeta: getQuestionRegistries.meta });
            dispatch({ type: SET_LOADING, loading: false });
            dispatch({ type: setIsLoading, isLoading: false });
            if (!searchText) {
                dispatch({
                    type: setLockedAddToRegistryQuestionCount,
                    lockedQuestionRegistryAddCount: getQuestionRegistries?.items?.length || 0
                });
            }
        },
        onError: (error) => {
            dispatch({ type: SET_LOADING, loading: false });
            dispatch({ type: setIsLoading, isLoading: false });
        },
        skip: true
    });

    useEffect(() => {
        if (companyData._id)
            if (checkIsFreemiumPlan(companyData.subscription.plan)) {
                getTotalLockedQuestionRegistriesCount({
                    variables: {
                        getLockedQuestionRegistriesCount: {
                            companyId: companyData._id
                        }
                    }
                });
            }
    }, [companyData, reload, searchText, location]);

    useEffect(() => {
        if (companyData._id) {
            if (templateType === 'all' || checkIsPremiumPlan(companyData?.subscription?.plan)) {
                dispatch({ type: SET_LOADING, loading: true });
                dispatch({ type: setIsLoading, isLoading: true });
                getQuestion({
                    variables: {
                        getQuestionRegistriesInput: {
                            companyId: companyData._id,
                            limit: itemsPerPage,
                            page: currentPage,
                            type: !isNotExtension ? 'all' : templateType,
                            searchText,
                            isFromExtension: !isNotExtension,
                            authorizedEmails: authorizedEmails,
                            userId: id
                        }
                    }
                });
                getQuestionIds({
                    variables: {
                        getQuestionRegistryIdsInput: {
                            companyId: companyData?._id,
                            type: templateType
                        }
                    }
                });
            } else {
                dispatch({ type: SET_LOADING, loading: false });
                dispatch({ type: setIsLoading, isLoading: false });
            }
        }
    }, [companyData, reload, itemsPerPage, currentPage, templateType, selectedItems, location, searchText]);

    const handleChange = (value) => {
        dispatch({ type: setSearchText, searchText: value });
        dispatch({ type: setCurrentPage, currentPage: 1 });
        dispatch({ type: setItemsPerPage, itemsPerPage: 5 });
    };

    const optimizedFn = useCallback(debounce(handleChange), []);
    let dataFreemium = {
        title: '',
        subTitle: '',
        image: '',
        buttonPosition: '',
        buttonName: ''
    };
    if (companyData)
        if (checkIsFreemiumPlan(companyData?.subscription?.plan || '') && templateType !== 'all') {
            switch (templateType) {
                case 'starred':
                    dataFreemium.buttonName = 'Upgrade';
                    dataFreemium.buttonPosition = 'left';
                    dataFreemium.image = starredQuestion;
                    dataFreemium.title = `Star questions in the question registry`;
                    dataFreemium.subTitle = `Have all of the important questions ready in one place.`;
                    break;
                case 'deleted':
                    dataFreemium.buttonName = 'Upgrade';
                    dataFreemium.buttonPosition = 'left';
                    dataFreemium.image = deleteQuestionIcon;
                    dataFreemium.title = `Restore deleted questions in the question registry`;
                    dataFreemium.subTitle = `We've all accidentally deleted something before.`;
                    break;
                case 'drafted':
                    dataFreemium.buttonName = 'Upgrade';
                    dataFreemium.buttonPosition = 'left';
                    dataFreemium.image = draftedQuestionIcon;
                    dataFreemium.title = `Draft unlimited questions in the question registry`;
                    dataFreemium.subTitle = `Easily collaborate and prepare question responses with your team to ensure you are ready for any question that comes your way.`;
                    break;
                default:
                    break;
            }
        }
    return (
        <>
            <Stack
                direction={{ md: 'row' }}
                alignItems="center"
                justifyContent={{ md: 'space-between' }}
                gap={2}
                className="kanban-question-heading"
            >
                <Typography color="#00214D" variant="h2" fontFamily="Poppins" fontWeight="600" className="dashboard-paper-header">
                    Question registry
                </Typography>

                <Box>
                    <TextField
                        name="Search"
                        fullWidth
                        placeholder="Search for a question"
                        sx={{
                            '.MuiInput-root': {
                                padding: '6px 15px',
                                minWidth: '346px'
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ fill: '#9E9E9E', width: '16px', height: '16px' }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchTextInput ? (
                                <InputAdornment
                                    position="start"
                                    className="text-cursored"
                                    onClick={() => {
                                        setSearchTextInput('');
                                        handleChange('');
                                    }}
                                >
                                    <Clear fontSize="small" />
                                </InputAdornment>
                            ) : null
                        }}
                        value={searchTextInput}
                        variant="standard"
                        onChange={(e) => {
                            setSearchTextInput(e.target.value);
                            optimizedFn(e.target.value);
                        }}
                        className="search-field"
                    />
                </Box>
            </Stack>

            <Paper className="content-section-paper" id="question-registry" sx={{ borderRadius: '0' }}>
                <Stack direction={{ md: 'row' }} gap={3} alignItems="flex-start">
                    <Box className="sidebar">
                        <ResponseTemplateSidebar isNotExtension={isNotExtension} />
                    </Box>

                    <Box className="question-content">
                        <Stack direction="row" gap="16px" flexWrap="wrap">
                            {/* {templateType === 'monthlyPrompt' && <MonthlyPrompt />} */}
                            {templateType !== 'monthlyPrompt' &&
                                (hasAdministrativeControl === true || canManageQuestion) &&
                                (templateType === 'all' || checkIsPremiumPlan(companyData?.subscription?.plan)) && (
                                    <ResponseTemplateContent isNotExtension={isNotExtension} />
                                )}
                            {templateType !== 'all' && (
                                <FreemiumPlanWrapper>
                                    <QuestionRegistryFremiumListing {...dataFreemium} type={templateType} />
                                </FreemiumPlanWrapper>
                            )}
                        </Stack>
                    </Box>
                </Stack>
                <ResponseTemplateModal />
                <QuestionCardInformation />
            </Paper>
        </>
    );
}

export default ResponseTemplate;
